import React, { useState, useEffect, useMemo, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import { toast } from "sonner";
import { Plus, Trash, WhatsappLogo, FloppyDisk, Share, CheckCircle, Boat, Scales, Gauge, Calendar, Clock, ChartLineUp, CloudSun, FilePdf } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";

function toIso(dateStr, timeStr, baseTimeStr) {
    if (!dateStr || !timeStr) return null;
    // If a baseTimeStr is provided and timeStr is earlier or equal, treat as next day
    // (e.g. window 22:00 → 00:30 means 00:30 is on the following calendar date).
    const dt = new Date(`${dateStr}T${timeStr}:00`);
    if (baseTimeStr && timeStr <= baseTimeStr) {
        dt.setDate(dt.getDate() + 1);
    }
    return dt.toISOString();
}

function pad(n) {
    return String(n).padStart(2, "0");
}
function isoToDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function isoToTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SubmitReportModal({ vessel, defaults, existingReport, onClose, onSubmitted }) {
    const { user } = useAuth();
    const isEdit = !!existingReport;
    const d = defaults || {};
    const init = existingReport
        ? {
              window_date: isoToDate(existingReport.window_start),
              window_start: isoToTime(existingReport.window_start),
              window_end: isoToTime(existingReport.window_end),
              tons_loaded: String(existingReport.tons_loaded ?? ""),
              draft_figure: existingReport.draft_figure != null ? String(existingReport.draft_figure) : "",
              notes: existingReport.notes || "",
              shift: existingReport.shift || "",
              hatch_no: existingReport.hatch_no ? String(existingReport.hatch_no) : "",
              equipment_used: existingReport.equipment_used || "",
          }
        : (() => {
              // Default to "now − 2h → now" for the 2-hourly window
              const now = new Date();
              const start = new Date(now.getTime() - 2 * 60 * 60 * 1000);
              return {
                  window_date: d.window_date || `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
                  window_start: d.window_start || `${pad(start.getHours())}:${pad(start.getMinutes())}`,
                  window_end: d.window_end || `${pad(now.getHours())}:${pad(now.getMinutes())}`,
                  tons_loaded: "",
                  draft_figure: "",
                  notes: "",
                  shift: (() => {
                      const h = now.getHours();
                      return h >= 6 && h < 18 ? "day" : "night";
                  })(),
                  hatch_no: vessel?.current_hatch ? String(vessel.current_hatch) : "",
                  equipment_used: "",
              };
          })();

    const [form, setForm] = useState(init);
    const [loading, setLoading] = useState(false);
    const [previousTob, setPreviousTob] = useState(
        existingReport ? (existingReport.previous_tob ?? 0) : 0
    );
    const [preview, setPreview] = useState(null);
    // Live pause state at the time of report generation — surfaced so the
    // supervisor & PDF/share snapshot clearly indicate the vessel is currently
    // standing (and why). Fetched from /metrics.pause_state on open.
    const [currentPause, setCurrentPause] = useState(null);
    // After submit we flip to a "snapshot" view with a WhatsApp share button.
    const [snapshot, setSnapshot] = useState(null);

    // Auto-fetch /report-preview on open (generate mode) — auto-fills window, tons, delays, flow rate
    useEffect(() => {
        if (isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/vessels/${vessel.id}/report-preview`);
                setPreview(data);
                setPreviousTob(Number(data.previous_tob || 0));
                // Pre-populate form unless caller already provided defaults
                setForm((f) => ({
                    ...f,
                    window_date: isoToDate(data.window_start),
                    window_start: isoToTime(data.window_start),
                    window_end: isoToTime(data.window_end),
                    tons_loaded:
                        data.tons_loaded_est != null
                            ? String(Math.round(data.tons_loaded_est))
                            : f.tons_loaded,
                }));
            } catch {
                // Fall back to legacy fetch if the preview endpoint is unreachable
                try {
                    const { data } = await api.get(`/vessels/${vessel.id}/reports`);
                    if (data.length === 0) {
                        setPreviousTob(0);
                    } else {
                        const last = data[data.length - 1];
                        const lastEff = last.effective_tob ?? last.draft_figure ?? last.tob ?? 0;
                        setPreviousTob(Number(lastEff));
                    }
                } catch { /* ignore */ }
            }
            // Live pause snapshot
            try {
                const { data } = await api.get(`/vessels/${vessel.id}/metrics`);
                if (data?.pause_state) setCurrentPause(data.pause_state);
            } catch { /* ignore */ }
        })();
    }, [vessel.id, isEdit]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const projectedTob = form.draft_figure !== ""
        ? parseFloat(form.draft_figure) || 0
        : previousTob + (parseFloat(form.tons_loaded) || 0);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                window_start: toIso(form.window_date, form.window_start),
                window_end: toIso(form.window_date, form.window_end, form.window_start),
                tons_loaded: parseFloat(form.tons_loaded),
                draft_figure: form.draft_figure !== "" ? parseFloat(form.draft_figure) : null,
                notes: form.notes,
                shift: form.shift || null,
                hatch_no: form.hatch_no ? parseInt(form.hatch_no) : null,
                equipment_used: form.equipment_used || null,
                delays: [],  // delays now live in the delays collection (Pause + Log delay)
            };
            let savedReport;
            if (isEdit) {
                const { data } = await api.patch(`/vessels/${vessel.id}/reports/${existingReport.id}`, payload);
                savedReport = data || { ...existingReport, ...payload };
                toast.success("Report updated");
            } else {
                const { data } = await api.post(`/vessels/${vessel.id}/reports`, payload);
                savedReport = data;
                toast.success("2-hourly report submitted");
            }
            // Pull the delays for the window for the snapshot
            let windowDelays = [];
            try {
                const m = await api.get(`/vessels/${vessel.id}/metrics`);
                const ws = new Date(payload.window_start).getTime();
                const we = new Date(payload.window_end).getTime();
                windowDelays = (m.data.all_delays || []).filter((dl) => {
                    if (!dl.start_time) return false;
                    const t = new Date(dl.start_time).getTime();
                    return t >= ws && t < we;
                });
            } catch { /* ignore */ }
            // Notify parent so the reports list refreshes
            onSubmitted?.();
            setSnapshot({
                report: savedReport,
                payload,
                previousTob: savedReport?.previous_tob ?? previousTob,
                newTob: savedReport?.effective_tob ?? (payload.draft_figure != null ? payload.draft_figure : previousTob + payload.tons_loaded),
                delays: windowDelays,
                currentPause,
                supervisorName: savedReport?.submitted_by_name || user?.name || "Supervisor",
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="report-modal">
            <div className={`g-card w-full max-h-[95vh] overflow-y-auto ${snapshot ? "max-w-6xl" : "max-w-3xl"}`}>
                <div className="g-card-header-dark sticky top-0">
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-slate-300">
                            {snapshot ? "Report submitted · share snapshot" : isEdit ? "Edit report" : "Generate 2 hourly report"}
                        </div>
                        <div className="font-display font-bold">{vessel.vessel_name}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-sm" data-testid="report-close">
                        Close
                    </button>
                </div>

                {snapshot ? (
                    <ReportSnapshot
                        vessel={vessel}
                        snapshot={snapshot}
                        onClose={onClose}
                    />
                ) : (
                <form onSubmit={submit} className="p-6 space-y-6" data-testid="report-form">
                    {!isEdit && currentPause && (
                        <CurrentlyStandingBanner pause={currentPause} />
                    )}
                    {!isEdit && preview && (
                        <div className="bg-[var(--grindrod-green-bg)] border-l-4 border-[var(--grindrod-green)] p-3 rounded-sm" data-testid="report-preview-banner">
                            <div className="uppercase-label text-[9px] text-[var(--grindrod-navy)]">Auto-generated from activities in this window</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 text-xs">
                                <div>
                                    <div className="text-slate-500 text-[10px] uppercase">Delays</div>
                                    <div className="font-bold text-[var(--grindrod-navy)] tabular">
                                        {preview.delays?.length || 0} <span className="font-normal text-slate-500">events</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-slate-500 text-[10px] uppercase">Total delay</div>
                                    <div className="font-bold text-[var(--grindrod-navy)] tabular">
                                        {fmtDuration(preview.delay_minutes_total || 0)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-slate-500 text-[10px] uppercase">Flow inputs</div>
                                    <div className="font-bold text-[var(--grindrod-navy)] tabular">
                                        {preview.flow_rate_inputs_in_window?.length || 0}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-slate-500 text-[10px] uppercase">Computed rate</div>
                                    <div className="font-bold text-[var(--grindrod-navy)] tabular">
                                        {Math.round(preview.computed_flow_rate_tph || 0).toLocaleString()} <span className="font-normal text-slate-500">t/h</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-2">
                                Review and adjust below — your changes will be saved when you submit.
                            </div>
                        </div>
                    )}
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <label className="uppercase-label block mb-1.5">Report Date</label>
                            <input type="date" className="g-input" value={form.window_date} onChange={set("window_date")} required data-testid="report-date" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Window Start</label>
                            <input type="time" className="g-input" value={form.window_start} onChange={set("window_start")} required data-testid="report-window-start" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Window End</label>
                            <input type="time" className="g-input" value={form.window_end} onChange={set("window_end")} required data-testid="report-window-end" />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="uppercase-label block mb-1.5">Tons loaded (past 2h)</label>
                            <input
                                type="number"
                                step="any"
                                inputMode="decimal"
                                className="g-input tabular"
                                value={form.tons_loaded}
                                onChange={set("tons_loaded")}
                                required
                                placeholder="1300"
                                data-testid="report-tons-loaded"
                            />
                            <div className="text-[10px] text-slate-500 mt-1">
                                Added to previous TOB ({Math.round(previousTob).toLocaleString()} t) to compute the new Tons On Board.
                            </div>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">
                                Draft figure <span className="text-slate-400 normal-case lowercase tracking-normal">(optional, from surveyor)</span>
                            </label>
                            <input
                                type="number"
                                step="any"
                                inputMode="decimal"
                                className="g-input tabular"
                                value={form.draft_figure}
                                onChange={set("draft_figure")}
                                placeholder="e.g. 45000"
                                data-testid="report-draft-figure"
                            />
                            <div className="text-[10px] text-[var(--grindrod-red)] mt-1 font-semibold">
                                When captured, this replaces the Tons On Board.
                            </div>
                        </div>
                    </div>

                    <div className="g-card p-4 bg-[var(--surface-alt)]" data-testid="projected-tob">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="uppercase-label text-[10px]">
                                    {form.draft_figure !== "" ? "New TOB (from draft survey)" : "New TOB (running total)"}
                                </div>
                                <div className="font-display font-black tabular text-3xl text-[var(--grindrod-navy)]">
                                    {Math.round(projectedTob).toLocaleString()} t
                                </div>
                            </div>
                            <div className="text-xs text-slate-500 text-right">
                                Previous TOB: <span className="tabular font-semibold">{Math.round(previousTob).toLocaleString()} t</span><br />
                                Target: <span className="tabular font-semibold">{vessel.tonnage.toLocaleString()} t</span>
                            </div>
                        </div>
                    </div>

                    <DelaysPreview
                        vesselId={vessel.id}
                        windowStartIso={form.window_date && form.window_start ? `${form.window_date}T${form.window_start}:00` : null}
                        windowEndIso={form.window_date && form.window_end ? (form.window_end <= form.window_start
                            ? new Date(new Date(`${form.window_date}T${form.window_end}:00`).getTime() + 86_400_000).toISOString().slice(0,16) + ":00"
                            : `${form.window_date}T${form.window_end}:00`) : null}
                    />

                    <div className="grid md:grid-cols-3 gap-4" data-testid="report-context-block">
                        <div>
                            <label className="uppercase-label block mb-1.5">Shift</label>
                            <select className="g-input" value={form.shift} onChange={set("shift")} data-testid="report-shift">
                                <option value="">—</option>
                                <option value="day">Day (06:00 – 18:00)</option>
                                <option value="night">Night (18:00 – 06:00)</option>
                            </select>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Hatch No.</label>
                            <select className="g-input" value={form.hatch_no} onChange={set("hatch_no")} data-testid="report-hatch">
                                <option value="">—</option>
                                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                                    <option key={n} value={n}>Hatch {n}</option>
                                ))}
                            </select>
                            {vessel?.current_hatch && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                    Vessel is currently loading on <b>H{vessel.current_hatch}</b>. Use the "Hatch · change" button on the live page to move hatches.
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Equipment used</label>
                            <input
                                type="text"
                                className="g-input"
                                value={form.equipment_used}
                                onChange={set("equipment_used")}
                                placeholder="e.g. Ship Loader 1, Belt 2"
                                data-testid="report-equipment"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="uppercase-label block mb-1.5">Notes (optional)</label>
                        <textarea className="g-input" rows={2} value={form.notes} onChange={set("notes")} placeholder="TPT: Nill" data-testid="report-notes" />
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="report-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="report-submit">
                            {loading ? (isEdit ? "Saving…" : "Submitting…") : isEdit ? "Save changes" : "Submit report"}
                        </button>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}

function DelaysPreview({ vesselId, windowStartIso, windowEndIso }) {
    const [delays, setDelays] = React.useState([]);
    const [loaded, setLoaded] = React.useState(false);
    React.useEffect(() => {
        if (!vesselId || !windowStartIso || !windowEndIso) return;
        (async () => {
            try {
                const { data } = await api.get(`/vessels/${vesselId}/metrics`);
                const all = data.all_delays || [];
                const ws = new Date(windowStartIso).getTime();
                const we = new Date(windowEndIso).getTime();
                setDelays(all.filter((d) => {
                    if (!d.start_time) return false;
                    const t = new Date(d.start_time).getTime();
                    return t >= ws && t < we;
                }));
            } catch {
                // ignore
            } finally {
                setLoaded(true);
            }
        })();
    }, [vesselId, windowStartIso, windowEndIso]);

    const sevColor = (s) => s === "major" ? "g-pill-red" : s === "minor" ? "g-pill-navy" : "g-pill-navy";
    const sevLabel = (s) => s === "major" ? "MAJOR" : s === "minor" ? "MINOR" : "STANDARD";

    return (
        <div data-testid="delays-preview">
            <div className="uppercase-label text-[var(--grindrod-red)]">Delays in this window</div>
            <div className="text-xs text-slate-500 mt-0.5 mb-3">
                Auto-pulled from <strong>Pause</strong> and <strong>Log delay</strong> entries within this 2-hour period — no manual entry required.
            </div>
            {!loaded ? (
                <div className="text-xs text-slate-400 border border-dashed border-slate-200 p-4 rounded-sm text-center">Loading…</div>
            ) : delays.length === 0 ? (
                <div className="text-xs text-slate-400 border border-dashed border-slate-200 p-4 rounded-sm text-center">
                    No delays in this window.
                </div>
            ) : (
                <div className="space-y-2">
                    {delays.map((d, i) => (
                        <div key={i} className="g-card p-3 flex items-center gap-3" data-testid={`delays-preview-row-${i}`}>
                            <span className={`g-pill ${sevColor(d.severity)}`} style={{ fontSize: 9, padding: "2px 7px" }}>
                                {sevLabel(d.severity)}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[var(--grindrod-navy)] truncate">{d.reason}</div>
                                {(d.actions_taken || d.location) && (
                                    <div className="text-xs text-slate-500 truncate">
                                        {d.actions_taken}{d.actions_taken && d.location ? " · " : ""}{d.location}
                                    </div>
                                )}
                            </div>
                            <div className="text-xs tabular text-right shrink-0">
                                <div className="font-semibold text-[var(--grindrod-navy)]">{fmtDuration(d.duration_minutes || 0)}</div>
                                <div className="text-slate-400 text-[10px]">{d.source}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function fmtHM(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDateLong(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function buildShareText({ vessel, snapshot }) {
    const { payload, previousTob, newTob, delays, supervisorName, loadingRate, progressPct, currentPause } = snapshot;
    const start = fmtHM(payload.window_start);
    const end = fmtHM(payload.window_end);
    const date = fmtDateLong(payload.window_start);
    const lines = [];
    lines.push(`*2-HOURLY VESSEL LOADING REPORT*`);
    lines.push(`Vessel: *${vessel.vessel_name}*${vessel.client_name ? ` (${vessel.client_name})` : ""}`);
    lines.push(`Window: ${date} · ${start} – ${end}`);
    lines.push(`Supervisor: ${supervisorName}`);
    if (currentPause) {
        const since = currentPause.started_at ? ` · since ${fmtHM(currentPause.started_at)}` : "";
        lines.push(`⚠ Currently standing — *${currentPause.reason || "Operational stop"}*${since}`);
        if (currentPause.description) lines.push(`   ${currentPause.description}`);
    }
    lines.push("");
    lines.push(`Tons loaded (2h): *${Math.round(payload.tons_loaded).toLocaleString()} t*`);
    lines.push(`Previous Total: ${Math.round(previousTob).toLocaleString()} t`);
    if (payload.draft_figure != null) {
        lines.push(`Draft figure (replaces TOB): *${Math.round(payload.draft_figure).toLocaleString()} t*`);
    }
    lines.push(`New Total On Board: *${Math.round(newTob).toLocaleString()} t* / ${Number(vessel.tonnage || 0).toLocaleString()} t (${progressPct.toFixed(2)}%)`);
    lines.push(`Loading rate (2h): *${Math.round(loadingRate).toLocaleString()} t/hr*`);
    if (payload.hatch_no) lines.push(`Hatch: H${payload.hatch_no}`);
    if (payload.equipment_used) lines.push(`Equipment: ${payload.equipment_used}`);
    if (payload.shift) lines.push(`Shift: ${payload.shift}`);
    lines.push("");
    if (delays && delays.length > 0) {
        lines.push(`*Delays in this window (${delays.length})*`);
        delays.forEach((d) => {
            const dur = fmtDuration(d.duration_minutes || 0);
            lines.push(`• ${d.reason || "Delay"} — ${dur}${d.actions_taken ? ` (${d.actions_taken})` : ""}`);
        });
    } else {
        lines.push(`No delays recorded in this window.`);
    }
    if (payload.notes) {
        lines.push("");
        lines.push(`Notes: ${payload.notes}`);
    }
    lines.push("");
    lines.push(`— Grindrod Terminals · Richards Bay`);
    return lines.join("\n");
}

/**
 * Build a "Detailed Log" of cumulative tonnage at 20-minute intervals across
 * the 2h window. We don't store per-20-min loads; we evenly distribute the
 * total tonnage across non-delay time. Delay overlaps zero out that bucket.
 */
function buildDetailedLog({ payload, delays, previousTob }) {
    const ws = new Date(payload.window_start).getTime();
    const we = new Date(payload.window_end).getTime();
    const totalMin = Math.max(1, (we - ws) / 60000);
    const bucketMin = 20;
    const bucketCount = Math.max(1, Math.round(totalMin / bucketMin));
    const delayMinTotal = (delays || []).reduce((s, d) => s + (d.duration_minutes || 0), 0);
    const productiveMin = Math.max(0.0001, totalMin - delayMinTotal);
    const tonsPerProductiveMin = payload.tons_loaded / productiveMin;
    let cumulative = previousTob;
    const rows = [];
    for (let i = 0; i < bucketCount; i++) {
        const bs = new Date(ws + i * bucketMin * 60000);
        const be = new Date(Math.min(we, ws + (i + 1) * bucketMin * 60000));
        // How much of this bucket is inside a delay window?
        let delayedMin = 0;
        let delayedReason = null;
        (delays || []).forEach((d) => {
            if (!d.start_time) return;
            const ds = new Date(d.start_time).getTime();
            const de = d.end_time ? new Date(d.end_time).getTime() : we;
            const ov = Math.max(0, (Math.min(be.getTime(), de) - Math.max(bs.getTime(), ds)) / 60000);
            if (ov > 0) {
                delayedMin += ov;
                delayedReason = d.reason || delayedReason;
            }
        });
        const bucketDuration = (be.getTime() - bs.getTime()) / 60000;
        const productive = Math.max(0, bucketDuration - delayedMin);
        const inc = Math.round(productive * tonsPerProductiveMin);
        cumulative += inc;
        rows.push({
            time: fmtHM(bs.toISOString()),
            incremental: inc,
            cumulative: Math.round(cumulative),
            remarks: i === 0 ? "Start of window" :
                     delayedMin > 0 ? `Delay: ${delayedReason || "loading paused"}` :
                     "Steady loading",
        });
    }
    // Final "End of window" row
    rows.push({
        time: fmtHM(new Date(we).toISOString()),
        incremental: 0,
        cumulative: Math.round(previousTob + payload.tons_loaded),
        remarks: "End of window",
    });
    return rows;
}

/**
 * Free open-meteo lookup for Richards Bay (no API key). Returns null on
 * any error so the snapshot still renders.
 */
async function fetchRichardsBayWeather() {
    try {
        const lat = -28.78;
        const lon = 32.04;
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m,visibility&wind_speed_unit=kmh`;
        const r = await fetch(url);
        if (!r.ok) return null;
        const j = await r.json();
        const cur = j.current || {};
        return {
            temperature: cur.temperature_2m,
            wind_kmh: cur.wind_speed_10m,
            wind_dir: cur.wind_direction_10m,
            visibility_km: cur.visibility != null ? cur.visibility / 1000 : null,
            condition: codeToCondition(cur.weather_code),
        };
    } catch {
        return null;
    }
}

function codeToCondition(code) {
    if (code == null) return "—";
    if (code === 0) return "Clear";
    if (code <= 3) return "Partly cloudy";
    if (code <= 48) return "Foggy";
    if (code <= 57) return "Drizzle";
    if (code <= 67) return "Rain";
    if (code <= 77) return "Snow";
    if (code <= 82) return "Rain showers";
    if (code <= 86) return "Snow showers";
    return "Thunderstorms";
}

function compass(deg) {
    if (deg == null) return "—";
    const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    return dirs[Math.round(deg / 45) % 8];
}

function ReportSnapshot({ vessel, snapshot, onClose }) {
    const { payload, previousTob, newTob, delays, supervisorName, currentPause } = snapshot;
    const cardRef = useRef(null);
    const [weather, setWeather] = useState(null);

    useEffect(() => {
        let mounted = true;
        fetchRichardsBayWeather().then((w) => { if (mounted) setWeather(w); });
        return () => { mounted = false; };
    }, []);

    const tonnage = Number(vessel.tonnage || 0);
    const progressPct = tonnage > 0 ? (newTob / tonnage) * 100 : 0;
    const windowHours = Math.max(0.01, (new Date(payload.window_end).getTime() - new Date(payload.window_start).getTime()) / 3600000);
    const loadingRate = payload.tons_loaded / windowHours;

    const detailedLog = useMemo(
        () => buildDetailedLog({ payload, delays, previousTob }),
        [payload, delays, previousTob]
    );
    const chartData = useMemo(() => detailedLog.map((r) => ({ time: r.time, tons: r.cumulative - previousTob })), [detailedLog, previousTob]);

    const enrichedSnapshot = { ...snapshot, loadingRate, progressPct };
    const shareText = buildShareText({ vessel, snapshot: enrichedSnapshot });
    const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            toast.success("Snapshot copied to clipboard");
        } catch {
            toast.error("Clipboard not available — long-press the box to copy manually");
        }
    };

    const nativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `2-Hourly Vessel Loading Report · ${vessel.vessel_name}`,
                    text: shareText,
                });
            } catch {
                /* user cancelled */
            }
        } else {
            copyToClipboard();
        }
    };

    const downloadPdf = () => {
        // Print the snapshot card via the browser's print dialog (Save as PDF).
        // The @media print stylesheet in App.css hides everything outside .report-print-area.
        document.body.classList.add("report-printing");
        const cleanup = () => {
            document.body.classList.remove("report-printing");
            window.removeEventListener("afterprint", cleanup);
        };
        window.addEventListener("afterprint", cleanup);
        setTimeout(() => window.print(), 200);
    };

    return (
        <div className="p-4 sm:p-6 space-y-4" data-testid="report-snapshot">
            <div className="text-center pb-1 print:hidden">
                <div className="inline-flex items-center gap-2 bg-[var(--grindrod-green-bg)] text-[var(--grindrod-green)] px-3 py-1.5 rounded-sm">
                    <CheckCircle size={16} weight="fill" />
                    <span className="font-display font-bold text-sm uppercase tracking-wide">Report submitted</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Saved to the vessel's 2-hourly report history.</div>
            </div>

            <div ref={cardRef} className="report-print-area g-card overflow-hidden shadow-lg" data-testid="snapshot-card">
                {/* Header */}
                <div className="bg-[var(--grindrod-navy)] text-white px-4 sm:px-6 py-4 sm:py-5 flex items-start gap-3 sm:gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-lg sm:text-2xl md:text-3xl tracking-tight leading-tight">
                            2-HOURLY VESSEL LOADING REPORT
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 sm:mt-3 text-xs sm:text-sm">
                            <div>
                                <div className="uppercase tracking-wide text-[10px] text-white/65 font-semibold">Report Generated</div>
                                <div className="font-semibold">{fmtDateLong(payload.window_end)} {fmtHM(payload.window_end)}</div>
                                <div className="text-[11px] text-white/85">Window: {fmtHM(payload.window_start)} – {fmtHM(payload.window_end)}</div>
                            </div>
                            <div>
                                <div className="uppercase tracking-wide text-[10px] text-white/65 font-semibold">Submitted By</div>
                                <div className="font-semibold">{supervisorName}</div>
                                {vessel.client_name && <div className="text-[11px] text-white/85">{vessel.client_name}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-sm">
                        <Boat size={22} weight="fill" />
                        <div className="font-display font-bold text-base sm:text-lg leading-tight">{vessel.vessel_name}</div>
                    </div>
                </div>

                <div className="p-4 sm:p-6 space-y-5 bg-white">
                    {currentPause && (
                        <CurrentlyStandingBanner pause={currentPause} compact />
                    )}
                    {/* KPI tiles */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="snapshot-kpis">
                        <Kpi icon={Scales} label="Tons Loaded (2H)" value={`${Math.round(payload.tons_loaded).toLocaleString()} t`} accent="navy" />
                        <Kpi icon={Gauge} label="New Total on Board" value={`${Math.round(newTob).toLocaleString()} t`} accent="green" />
                        <Kpi icon={Calendar} label="Shift" value={(payload.shift || "—").toString().replace(/^./, (c) => c.toUpperCase())} />
                        <Kpi icon={Clock} label="Previous Total" value={`${Math.round(previousTob).toLocaleString()} t`} />
                        <Kpi icon={ChartLineUp} label="Target (Voyage)" value={`${tonnage.toLocaleString()} t`} />
                    </div>

                    {/* Progress + Rate */}
                    <div className="grid gap-3 lg:grid-cols-3">
                        <div className="g-card p-4 lg:col-span-2" data-testid="snapshot-progress">
                            <div className="flex items-center justify-between">
                                <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)]">Loading Progress</div>
                                <div className="font-display font-black text-[var(--grindrod-green)] text-2xl tabular">
                                    {progressPct.toFixed(2)}%
                                </div>
                            </div>
                            <div className="mt-2 h-3 bg-slate-100 rounded-sm overflow-hidden">
                                <div className="h-full bg-[var(--grindrod-green)]" style={{ width: `${Math.min(100, progressPct)}%` }} />
                            </div>
                            <div className="text-xs text-slate-500 mt-1.5">
                                {Math.round(newTob).toLocaleString()} t Loaded / {tonnage.toLocaleString()} t Target
                            </div>
                            <div className="text-[10px] text-slate-400 mt-3 flex justify-between tabular">
                                <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                            </div>
                        </div>
                        <div className="g-card p-4" data-testid="snapshot-rate">
                            <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)]">Loading Rate (2H)</div>
                            <div className="mt-2 flex items-baseline gap-2">
                                <div className="font-display font-black text-[var(--grindrod-navy)] text-4xl sm:text-5xl tabular">
                                    {Math.round(loadingRate).toLocaleString()}
                                </div>
                                <div className="text-base font-semibold text-slate-500">t/hr</div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Average Loading Rate</div>
                        </div>
                    </div>

                    {/* Chart + Cargo Summary */}
                    <div className="grid gap-3 lg:grid-cols-3">
                        <div className="g-card p-4 lg:col-span-2" data-testid="snapshot-chart">
                            <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)] mb-2">Loading Over Time (This Window)</div>
                            <div style={{ width: "100%", height: 220 }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData} margin={{ top: 18, right: 10, left: -10, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                        <XAxis dataKey="time" stroke="#64748B" fontSize={10} tickLine={false} />
                                        <YAxis stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                                        <Tooltip
                                            formatter={(v) => `${Math.round(v).toLocaleString()} t`}
                                            contentStyle={{ fontSize: 11, borderRadius: 2 }}
                                        />
                                        <Bar dataKey="tons" fill="var(--grindrod-green)" radius={[4, 4, 0, 0]} maxBarSize={36}>
                                            <LabelList dataKey="tons" position="top" formatter={(v) => v > 0 ? `${Math.round(v).toLocaleString()} t` : ""} style={{ fontSize: 10, fill: "var(--grindrod-navy)", fontWeight: 600 }} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="text-[10px] text-center text-slate-500 mt-1">Cumulative Tonnage</div>
                        </div>
                        <div className="g-card p-4" data-testid="snapshot-cargo">
                            <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)] mb-2">Cargo Summary</div>
                            <SummaryRow label="Cargo" value={vessel.product || "—"} />
                            <SummaryRow label="Tons Loaded (2H)" value={`${Math.round(payload.tons_loaded).toLocaleString()} t`} bold />
                            <SummaryRow label="Previous Total (2H Ago)" value={`${Math.round(previousTob).toLocaleString()} t`} />
                            <SummaryRow label="New Total on Board" value={`${Math.round(newTob).toLocaleString()} t`} bold green />
                            <SummaryRow label="Target (Voyage)" value={`${tonnage.toLocaleString()} t`} />
                            <SummaryRow label="% of Target Achieved" value={`${progressPct.toFixed(2)}%`} green />
                            <div className="text-[10px] text-slate-400 mt-2">All weights in metric tons (t)</div>
                        </div>
                    </div>

                    {/* Detailed Log */}
                    <div className="g-card overflow-hidden" data-testid="snapshot-detailed-log">
                        <div className="px-4 py-2.5 border-b border-slate-100 uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)]">
                            Detailed Log (2-Hour Window)
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[var(--grindrod-navy)] text-white">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-[10px]">Time</th>
                                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wide text-[10px]">Incremental (t)</th>
                                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wide text-[10px]">Cumulative (t)</th>
                                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-[10px]">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailedLog.map((row, i) => (
                                        <tr key={i} className="border-t border-slate-100" data-testid={`snapshot-log-row-${i}`}>
                                            <td className="px-3 py-1.5 tabular text-slate-700">{row.time}</td>
                                            <td className="px-3 py-1.5 tabular text-right text-slate-700">{row.incremental.toLocaleString()}</td>
                                            <td className="px-3 py-1.5 tabular text-right font-semibold text-[var(--grindrod-green)]">{row.cumulative.toLocaleString()}</td>
                                            <td className="px-3 py-1.5 text-slate-600">{row.remarks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Notes + Weather */}
                    <div className="grid gap-3 lg:grid-cols-2">
                        <div className="g-card p-4" data-testid="snapshot-notes">
                            <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)] mb-2">Notes</div>
                            <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
                                {(!delays || delays.length === 0) ? (
                                    <li>No delays recorded in this window.</li>
                                ) : (
                                    delays.map((d, i) => (
                                        <li key={i}>
                                            <span className="font-semibold text-[var(--grindrod-navy)]">{d.reason || "Delay"}</span> · {fmtDuration(d.duration_minutes || 0)}{d.actions_taken ? ` · ${d.actions_taken}` : ""}
                                        </li>
                                    ))
                                )}
                                {payload.notes && <li><span className="font-semibold text-[var(--grindrod-navy)]">Supervisor note:</span> {payload.notes}</li>}
                                <li>All figures are unaudited.</li>
                                <li className="text-slate-500">— Grindrod Terminals — Richards Bay</li>
                            </ul>
                        </div>
                        <div className="g-card p-4" data-testid="snapshot-weather">
                            <div className="uppercase-label text-[10px] font-bold text-[var(--grindrod-navy)] mb-2">Weather (at report time)</div>
                            <div className="flex items-center gap-3">
                                <CloudSun size={36} weight="fill" className="text-slate-400 shrink-0" />
                                {weather ? (
                                    <div className="flex-1">
                                        <div className="font-display font-black text-2xl text-[var(--grindrod-navy)]">{Math.round(weather.temperature)}°C</div>
                                        <div className="text-sm text-slate-600">{weather.condition}</div>
                                    </div>
                                ) : (
                                    <div className="flex-1 text-sm text-slate-400 italic">Fetching live weather…</div>
                                )}
                                {weather && (
                                    <div className="text-xs text-slate-600 space-y-0.5 text-right">
                                        <div><span className="text-slate-400">Wind</span> <span className="font-semibold tabular">{Math.round(weather.wind_kmh || 0)} km/h {compass(weather.wind_dir)}</span></div>
                                        <div><span className="text-slate-400">Visibility</span> <span className="font-semibold tabular">{weather.visibility_km != null ? `${weather.visibility_km.toFixed(0)} km` : "—"}</span></div>
                                    </div>
                                )}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-2">Source: open-meteo · Richards Bay</div>
                        </div>
                    </div>

                    <div className="text-center text-[10px] text-slate-400 italic">This report is system generated.</div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 print:hidden">
                <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB955] text-white font-bold uppercase tracking-wide text-sm px-4 py-2.5 rounded-sm"
                    data-testid="snapshot-share-whatsapp"
                >
                    <WhatsappLogo size={16} weight="fill" /> Share via WhatsApp
                </a>
                <button
                    onClick={nativeShare}
                    className="btn-outline inline-flex items-center gap-2 text-sm"
                    data-testid="snapshot-share-native"
                >
                    <Share size={14} weight="fill" /> Share report
                </button>
                <button
                    onClick={downloadPdf}
                    className="btn-outline inline-flex items-center gap-2 text-sm"
                    data-testid="snapshot-download-pdf"
                >
                    <FilePdf size={14} weight="fill" /> Download PDF
                </button>
                <button
                    onClick={copyToClipboard}
                    className="btn-outline inline-flex items-center gap-2 text-sm"
                    data-testid="snapshot-copy"
                >
                    <FloppyDisk size={14} weight="fill" /> Copy text
                </button>
                <button
                    onClick={onClose}
                    className="ml-auto btn-primary text-sm"
                    data-testid="snapshot-done"
                >
                    Done
                </button>
            </div>
        </div>
    );
}


function Kpi({ icon: Icon, label, value, accent }) {
    const valueClass =
        accent === "green" ? "text-[var(--grindrod-green)]"
        : accent === "navy" ? "text-[var(--grindrod-navy)]"
        : "text-slate-800";
    return (
        <div className="g-card p-2.5 sm:p-3 flex items-center gap-2.5">
            {Icon && <Icon size={20} weight="fill" className="text-[var(--grindrod-navy)] shrink-0" />}
            <div className="min-w-0 flex-1">
                <div className="uppercase tracking-wide text-[9px] sm:text-[10px] font-semibold text-slate-500 leading-tight">{label}</div>
                <div className={`font-display font-black tabular text-base sm:text-lg leading-tight ${valueClass}`}>{value}</div>
            </div>
        </div>
    );
}


function SummaryRow({ label, value, bold, green }) {
    return (
        <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-slate-500">{label}</span>
            <span className={`tabular ${bold ? "font-display font-bold" : "font-semibold"} ${green ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-navy)]"}`}>{value}</span>
        </div>
    );
}

/**
 * "Currently standing" banner — surfaced both inside the report form (while
 * the supervisor is generating it) and inside the report snapshot/PDF so
 * downstream readers see at a glance that the vessel was paused at the
 * moment the report was issued, and why.
 */
function CurrentlyStandingBanner({ pause, compact }) {
    const startedAt = pause?.started_at ? new Date(pause.started_at) : null;
    const standingMin = startedAt ? Math.max(0, Math.round((Date.now() - startedAt.getTime()) / 60000)) : null;
    const standingLabel = standingMin != null ? fmtDuration(standingMin) : null;
    const eta = pause?.eta_minutes != null ? fmtDuration(pause.eta_minutes) : null;
    return (
        <div
            className={`border-l-4 border-[var(--grindrod-red)] bg-[#FFF4F4] rounded-sm ${compact ? "p-3" : "p-4"}`}
            data-testid="currently-standing-banner"
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[var(--grindrod-red)] text-white flex items-center justify-center font-bold">!</div>
                <div className="flex-1 min-w-0">
                    <div className="uppercase tracking-wide text-[10px] font-bold text-[var(--grindrod-red)]">
                        Currently standing — vessel is paused at report time
                    </div>
                    <div className="font-display font-black text-[var(--grindrod-navy)] text-base sm:text-lg leading-tight mt-0.5 truncate">
                        {pause?.reason || "Operational stop"}
                    </div>
                    {pause?.description && (
                        <div className="text-xs sm:text-sm text-slate-700 mt-1 whitespace-pre-wrap">{pause.description}</div>
                    )}
                    <div className="text-[11px] text-slate-600 mt-2 flex flex-wrap gap-x-4 gap-y-1 tabular">
                        {startedAt && (
                            <span><span className="text-slate-400">Standing since</span> <span className="font-semibold">{fmtHM(pause.started_at)}</span></span>
                        )}
                        {standingLabel && (
                            <span><span className="text-slate-400">Standing for</span> <span className="font-semibold">{standingLabel}</span></span>
                        )}
                        {eta && (
                            <span><span className="text-slate-400">ETA repair</span> <span className="font-semibold">{eta}</span></span>
                        )}
                        {pause?.started_by_name && (
                            <span><span className="text-slate-400">Logged by</span> <span className="font-semibold">{pause.started_by_name}</span></span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

